import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useHeader } from '../contexts/HeaderContext';
import { API_URL } from '../config';
import { FilePreview } from '../components/FileViewer';
import { FileText, RefreshCw, X } from 'lucide-react';

// --- Рабочие дни (для вкладки Общее) ---
const isWorkingDay = (date) => {
  const day = date.getDay();
  return day >= 1 && day <= 5;
};

const getNextWorkingDay = (date) => {
  let nextDay = new Date(date);
  do {
    nextDay.setDate(nextDay.getDate() + 1);
  } while (!isWorkingDay(nextDay));
  return nextDay;
};

const getOrderStartDate = (createdAt) => {
  const orderDate = new Date(createdAt);
  const orderHour = orderDate.getHours();
  if (orderHour >= 12) return getNextWorkingDay(orderDate);
  if (isWorkingDay(orderDate)) return orderDate;
  return getNextWorkingDay(orderDate);
};

const getWorkingDaysFromOrderStart = (createdAt) => {
  const orderDate = new Date(createdAt);
  const orderHour = orderDate.getHours();
  const today = new Date();
  if (orderDate.toDateString() === today.toDateString() && orderHour >= 12) return 0;
  if (orderDate.toDateString() === today.toDateString() && orderHour < 12) return 1;
  const startDate = getOrderStartDate(createdAt);
  let current = new Date(startDate);
  let workingDays = 0;
  if (startDate > today) return 0;
  while (current < today) {
    if (isWorkingDay(current)) workingDays++;
    current.setDate(current.getDate() + 1);
  }
  if (isWorkingDay(today) && startDate <= today) workingDays++;
  return workingDays;
};

const formatOrderNumber = (orderNumber, title, stageName) => {
  const isRework = title && (
    title.toLowerCase().includes('переделка') ||
    title.toLowerCase().includes('бой') ||
    (stageName && stageName.toLowerCase().includes('закалка') && title.toLowerCase().includes('бой'))
  );
  return isRework ? `${orderNumber}П` : orderNumber;
};

// --- Индивидуальная вкладка: время на стадии ---
const getHoursOnStage = (movedAt) => {
  if (!movedAt) return 0;
  const diff = new Date() - new Date(movedAt);
  return diff / (1000 * 60 * 60);
};

const formatDuration = (hours) => {
  if (hours < 1) {
    const mins = Math.floor(hours * 60);
    return `${mins}м`;
  }
  const h = Math.floor(hours);
  const m = Math.floor((hours - h) * 60);
  if (h >= 24) {
    const d = Math.floor(h / 24);
    const rh = h % 24;
    return rh > 0 ? `${d}д ${rh}ч` : `${d}д`;
  }
  return m > 0 ? `${h}ч ${m}м` : `${h}ч`;
};

// --- Стадии ---
const ALL_STAGE_NAMES = [
  'Резка стекла',
  'Обработка стекла',
  'Готово к закалке',
  'Стекло на закалке',
  'Матировка',
  'Сборка лофта / зеркала',
  'Упаковка',
  'Самовывоз',
  'Отправка',
  'Новый заказ',
  'Закупка',
  'Готово склад',
];

// Стадии, которые всегда серые на вкладке Общее
const GREY_STAGES = ['самовывоз', 'комплектация', 'готово', 'склад'];

const getDefaultGeneralSettings = () => {
  const defaults = {};
  ALL_STAGE_NAMES.forEach(name => {
    defaults[name] = { greenDays: 3, yellowDays: 6 };
  });
  return defaults;
};

const getDefaultIndividualSettings = () => {
  const defaults = {};
  ALL_STAGE_NAMES.forEach(name => {
    defaults[name] = { greenHours: 3, yellowHours: 6 };
  });
  return defaults;
};

const mergeSettings = (saved) => {
  const defGeneral = getDefaultGeneralSettings();
  const defIndividual = getDefaultIndividualSettings();

  // Поддержка старого формата (плоский объект с greenHours)
  if (saved && !saved.general && !saved.individual) {
    return {
      general: defGeneral,
      individual: { ...defIndividual, ...saved },
    };
  }
  return {
    general: { ...defGeneral, ...(saved?.general || {}) },
    individual: { ...defIndividual, ...(saved?.individual || {}) },
  };
};

const OrderDrawingsModal = ({ order, data, loading, error, onClose, onRetry }) => {
  const [activeFileKey, setActiveFileKey] = useState('');
  const drawingFiles = data ? [
    ...(data.productionFiles || []).map(file => ({ ...file, section: 'Деталировка', key: `production-${file.id}` })),
    ...(data.designFiles || []).map(file => ({ ...file, section: 'Дизайн', key: `design-${file.id}` })),
  ] : [];

  useEffect(() => {
    const firstProductionFile = data?.productionFiles?.[0];
    const firstDesignFile = data?.designFiles?.[0];
    setActiveFileKey(
      firstProductionFile
        ? `production-${firstProductionFile.id}`
        : firstDesignFile
          ? `design-${firstDesignFile.id}`
          : ''
    );
  }, [data]);

  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const activeFile = drawingFiles.find(file => file.key === activeFileKey) || drawingFiles[0];

  return (
    <div
      className="fixed inset-0 z-[1200] flex items-center justify-center bg-gray-950/55 p-3 sm:p-6"
      onMouseDown={event => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
        <div className="flex shrink-0 items-center justify-between gap-4 border-b border-gray-200 px-4 py-3 sm:px-5">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 shrink-0 text-blue-600" />
              <h2 className="truncate text-base font-semibold text-gray-900 sm:text-lg">
                Чертежи заказа {data?.orderNumber || order?.orderNumber || order?.id}
              </h2>
            </div>
            {(data?.title || order?.title) && (
              <p className="mt-0.5 truncate pl-7 text-xs text-gray-500">{data?.title || order?.title}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
            aria-label="Закрыть окно чертежей"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
          {loading && (
            <div className="flex min-h-[320px] flex-col items-center justify-center text-sm text-gray-500">
              <div className="mb-3 h-8 w-8 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600" />
              Загружаем чертежи из Bitrix24…
            </div>
          )}

          {!loading && error && (
            <div className="flex min-h-[260px] flex-col items-center justify-center text-center">
              <div className="mb-3 rounded-full bg-red-50 p-3 text-red-500">
                <FileText className="h-6 w-6" />
              </div>
              <p className="mb-4 max-w-md text-sm text-gray-600">{error}</p>
              <button
                type="button"
                onClick={onRetry}
                className="inline-flex h-10 items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 text-sm font-medium text-blue-700 hover:bg-blue-100"
              >
                <RefreshCw className="h-4 w-4" /> Повторить
              </button>
            </div>
          )}

          {!loading && !error && drawingFiles.length === 0 && (
            <div className="flex min-h-[260px] flex-col items-center justify-center text-center text-gray-500">
              <FileText className="mb-3 h-8 w-8 text-gray-300" />
              <p className="text-sm font-medium text-gray-700">У заказа нет загруженных чертежей</p>
              <p className="mt-1 text-xs">Проверьте поля деталировки и дизайна в Bitrix24.</p>
            </div>
          )}

          {!loading && !error && activeFile && (
            <>
              <div className="mb-4 flex flex-wrap gap-2">
                {drawingFiles.map((file, index) => (
                  <button
                    key={file.key}
                    type="button"
                    onClick={() => setActiveFileKey(file.key)}
                    className={`rounded-lg border px-3 py-2 text-left text-xs transition-colors ${activeFile.key === file.key
                      ? 'border-blue-300 bg-blue-50 text-blue-700'
                      : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}
                  >
                    <span className="block font-medium">{file.section}{drawingFiles.length > 1 ? ` ${index + 1}` : ''}</span>
                    <span className="block max-w-[220px] truncate text-[11px] opacity-75">{file.name || `Файл ${file.id}`}</span>
                  </button>
                ))}
              </div>
              <FilePreview
                key={`${data.id}-${activeFile.key}`}
                // Загружаем через серверный proxy: Bitrix не всегда отдает расширение
                // в прямой ссылке, поэтому браузер может принять PDF за картинку.
                file={{ ...activeFile, url: null }}
                dealId={data.id}
                index={drawingFiles.findIndex(file => file.key === activeFile.key)}
                totalFiles={drawingFiles.length}
                onError={() => {}}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// --- Модальное окно настроек ---
const SettingsModal = ({ settings, onSave, onClose, saving }) => {
  const [activeTab, setActiveTab] = useState('general');
  const [localGeneral, setLocalGeneral] = useState(() =>
    JSON.parse(JSON.stringify(settings.general))
  );
  const [localIndividual, setLocalIndividual] = useState(() =>
    JSON.parse(JSON.stringify(settings.individual))
  );

  const handleChangeGeneral = (stageName, field, value) => {
    setLocalGeneral(prev => ({
      ...prev,
      [stageName]: { ...prev[stageName], [field]: parseFloat(value) || 0 },
    }));
  };

  const handleChangeIndividual = (stageName, field, value) => {
    setLocalIndividual(prev => ({
      ...prev,
      [stageName]: { ...prev[stageName], [field]: parseFloat(value) || 0 },
    }));
  };

  const handleSave = () => {
    onSave({ general: localGeneral, individual: localIndividual });
  };

  const handleReset = () => {
    if (activeTab === 'general') setLocalGeneral(getDefaultGeneralSettings());
    else setLocalIndividual(getDefaultIndividualSettings());
  };

  const isGreyStage = (name) =>
    GREY_STAGES.some(g => name.toLowerCase().includes(g));

  return (
    <div className="fixed inset-0 z-[1100] flex items-end sm:items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-2xl w-full sm:max-w-2xl sm:mx-4 flex flex-col max-h-[90vh]">
        {/* Шапка */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-bold text-gray-900">Настройки сроков</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors text-2xl leading-none ml-4">
            &times;
          </button>
        </div>

        {/* Вкладки внутри модалки */}
        <div className="flex border-b border-gray-200 flex-shrink-0 px-5">
          <button
            onClick={() => setActiveTab('general')}
            className={`py-2.5 px-4 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'general'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Общее (рабочие дни)
          </button>
          <button
            onClick={() => setActiveTab('individual')}
            className={`py-2.5 px-4 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'individual'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Индивидуально (часы)
          </button>
        </div>

        {/* Контент — прокручивается */}
        <div className="overflow-y-auto flex-1 px-5 py-4">
          {activeTab === 'general' ? (
            <>
              <p className="text-xs text-gray-500 mb-4">
                Цвет заказа на вкладке «Общее» зависит от числа рабочих дней с момента создания заказа. Серые стадии (Самовывоз, Готово склад и т.д.) не закрашиваются.
              </p>
              <div className="space-y-2">
                {ALL_STAGE_NAMES.map(stageName => {
                  const s = localGeneral[stageName] || { greenDays: 3, yellowDays: 6 };
                  const grey = isGreyStage(stageName);
                  return (
                    <div key={stageName} className={`p-3 rounded-lg ${grey ? 'bg-gray-100 opacity-60' : 'bg-gray-50'}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm font-medium text-gray-800">{stageName}</span>
                        {grey && <span className="text-xs text-gray-400 italic">всегда серая</span>}
                      </div>
                      {!grey && (
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                          <div className="flex items-center gap-1.5">
                            <span className="w-2.5 h-2.5 rounded-full bg-green-500 flex-shrink-0"></span>
                            <span className="text-xs text-gray-500">до</span>
                            <input
                              type="number" min="0" step="1"
                              value={s.greenDays}
                              onChange={e => handleChangeGeneral(stageName, 'greenDays', e.target.value)}
                              className="w-14 border border-gray-300 rounded px-2 py-1 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-400"
                            />
                            <span className="text-xs text-gray-500">дн.</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="w-2.5 h-2.5 rounded-full bg-yellow-400 flex-shrink-0"></span>
                            <span className="text-xs text-gray-500">до</span>
                            <input
                              type="number" min="0" step="1"
                              value={s.yellowDays}
                              onChange={e => handleChangeGeneral(stageName, 'yellowDays', e.target.value)}
                              className="w-14 border border-gray-300 rounded px-2 py-1 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-400"
                            />
                            <span className="text-xs text-gray-500">дн.</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="w-2.5 h-2.5 rounded-full bg-red-500 flex-shrink-0"></span>
                            <span className="text-xs text-gray-500 whitespace-nowrap">от {s.yellowDays} дн.</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              <p className="text-xs text-gray-500 mb-4">
                Цвет заказа на вкладке «Индивидуально» зависит от времени на текущей стадии с момента перехода.
              </p>
              <div className="space-y-2">
                {ALL_STAGE_NAMES.map(stageName => {
                  const s = localIndividual[stageName] || { greenHours: 3, yellowHours: 6 };
                  return (
                    <div key={stageName} className="p-3 bg-gray-50 rounded-lg">
                      <div className="text-sm font-medium text-gray-800 mb-2">{stageName}</div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                        <div className="flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-full bg-green-500 flex-shrink-0"></span>
                          <span className="text-xs text-gray-500">до</span>
                          <input
                            type="number" min="0" step="0.5"
                            value={s.greenHours}
                            onChange={e => handleChangeIndividual(stageName, 'greenHours', e.target.value)}
                            className="w-14 border border-gray-300 rounded px-2 py-1 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-400"
                          />
                          <span className="text-xs text-gray-500">ч</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-full bg-yellow-400 flex-shrink-0"></span>
                          <span className="text-xs text-gray-500">до</span>
                          <input
                            type="number" min="0" step="0.5"
                            value={s.yellowHours}
                            onChange={e => handleChangeIndividual(stageName, 'yellowHours', e.target.value)}
                            className="w-14 border border-gray-300 rounded px-2 py-1 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-400"
                          />
                          <span className="text-xs text-gray-500">ч</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-full bg-red-500 flex-shrink-0"></span>
                          <span className="text-xs text-gray-500 whitespace-nowrap">от {s.yellowHours}ч</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Подвал */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200 flex-shrink-0">
          <button onClick={handleReset} className="text-sm text-gray-500 hover:text-gray-700 underline">
            Сбросить вкладку
          </button>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Отмена
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
            >
              {saving && (
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                </svg>
              )}
              {saving ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Главный компонент ---
const OrdersDashboardPage = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settings, setSettings] = useState(() => mergeSettings(null));
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [drawingModal, setDrawingModal] = useState({ order: null, data: null, loading: false, error: '' });
  const drawingsCacheRef = useRef(new Map());
  const drawingsRequestRef = useRef(0);
  const { user } = useAuth();
  const { setHeaderExtras } = useHeader();

  const currentUser = user || JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = currentUser.role;
  const username = currentUser.username || '';
  const isFranchise = userRole === 'franchise';
  const userPrefix = username.substring(0, 4).toLowerCase();
  const isAdmin = userRole === 'admin' || userRole === 'adminb24';

  // Поиск в хедере
  useEffect(() => {
    setHeaderExtras(
      <input
        type="text"
        placeholder="Поиск по заказу..."
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        className="border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
        style={{ width: '200px', fontSize: '16px' }}
      />
    );
    return () => setHeaderExtras(null);
  }, [searchTerm, setHeaderExtras]);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`
  });

  const closeDrawingModal = useCallback(() => {
    drawingsRequestRef.current += 1;
    setDrawingModal({ order: null, data: null, loading: false, error: '' });
  }, []);

  const loadOrderDrawings = useCallback(async (order, force = false) => {
    if (!order?.id) return;

    const cacheKey = String(order.id);
    const requestId = ++drawingsRequestRef.current;
    const cached = drawingsCacheRef.current.get(cacheKey);
    if (cached && !force) {
      setDrawingModal({ order, data: cached, loading: false, error: '' });
      return;
    }

    setDrawingModal({ order, data: null, loading: true, error: '' });
    try {
      const response = await axios.get(`${API_URL}/api/orders-dashboard/${order.id}/drawings`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      if (drawingsRequestRef.current !== requestId) return;

      const drawingData = response.data?.order;
      if (!response.data?.success || !drawingData) {
        throw new Error(response.data?.error || 'Не удалось получить чертежи заказа');
      }

      drawingsCacheRef.current.set(cacheKey, drawingData);
      setDrawingModal({ order, data: drawingData, loading: false, error: '' });
    } catch (drawingError) {
      if (drawingsRequestRef.current !== requestId) return;
      setDrawingModal({
        order,
        data: null,
        loading: false,
        error: drawingError.response?.data?.error || drawingError.message || 'Не удалось загрузить чертежи',
      });
    }
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [dashRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/orders-dashboard`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/dashboard-stage-settings`, { headers: getAuthHeaders() }),
      ]);
      if (dashRes.data.success) {
        setDashboardData(dashRes.data.data);
      } else {
        throw new Error(dashRes.data.error || 'Ошибка получения данных');
      }
      if (settingsRes.data.success) {
        setSettings(mergeSettings(settingsRes.data.data));
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDashboardData(); }, []);

  const handleSaveSettings = useCallback(async (newSettings) => {
    setSettingsSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/dashboard-stage-settings`,
        { settings: newSettings },
        { headers: getAuthHeaders() }
      );
      setSettings(mergeSettings(newSettings));
      setShowSettingsModal(false);
    } catch (err) {
      console.error('Ошибка сохранения настроек:', err);
      alert('Не удалось сохранить настройки на сервере');
    } finally {
      setSettingsSaving(false);
    }
  }, []);

  // Цвет для Общего (рабочие дни)
  const getGeneralColor = useCallback((workingDays, stageName) => {
    const stageKey = stageName?.toLowerCase() || '';
    if (GREY_STAGES.some(g => stageKey.includes(g))) return '#6B7280';
    const s = settings.general[stageName] || { greenDays: 3, yellowDays: 6 };
    if (workingDays <= s.greenDays) return '#10B981';
    if (workingDays <= s.yellowDays) return '#F59E0B';
    return '#EF4444';
  }, [settings.general]);

  // Цвет для Индивидуального (часы)
  const getIndividualColor = useCallback((hours, stageName) => {
    const s = settings.individual[stageName] || { greenHours: 3, yellowHours: 6 };
    if (hours <= s.greenHours) return '#10B981';
    if (hours <= s.yellowHours) return '#F59E0B';
    return '#EF4444';
  }, [settings.individual]);

  // Фильтрация по поиску
  const filterOrders = (orders) => {
    if (!searchTerm.trim()) return orders;
    const q = searchTerm.toLowerCase();
    return orders.filter(o => {
      const num = (o.orderNumber || o.id || '').toString().toLowerCase();
      const title = (o.title || '').toLowerCase();
      return num.includes(q) || title.includes(q);
    });
  };

  // --- Общая вкладка: рендер стадии ---
  const renderGeneralStage = (stage) => {
    let ordersToShow = stage.orders || [];
    if (isFranchise && userPrefix) {
      ordersToShow = ordersToShow.filter(o => (o.orderNumber || o.id || '').toString().toLowerCase().startsWith(userPrefix));
    }
    ordersToShow = filterOrders(ordersToShow);

    const ordersWithColors = ordersToShow.map(order => {
      const workingDays = getWorkingDaysFromOrderStart(order.createdAt || order.createdTime);
      return { ...order, color: getGeneralColor(workingDays, stage.name), workingDays };
    });
    const s = settings.general[stage.name] || { greenDays: 3, yellowDays: 6 };
    const isGrey = GREY_STAGES.some(g => stage.name.toLowerCase().includes(g));

    return (
      <div key={stage.stage} className="bg-white rounded-lg shadow-md border-l-4 hover:shadow-lg transition-shadow duration-200"
           style={{ borderLeftColor: stage.color }}>
        <div className="p-3 sm:p-6">
          <div className="flex items-center justify-between mb-2 sm:mb-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full flex-shrink-0" style={{ backgroundColor: stage.color }}></div>
              <h3 className="text-sm sm:text-lg font-semibold text-gray-800">{stage.name}</h3>
            </div>
            <div className="flex items-center space-x-1">
              <span className="text-lg sm:text-2xl font-bold" style={{ color: stage.color }}>
                {isFranchise ? ordersWithColors.length : stage.count}
              </span>
              <span className="text-xs sm:text-sm text-gray-500">зак.</span>
            </div>
          </div>
          {/* Пороги */}
          {!isGrey && !isFranchise && (
            <div className="flex gap-1.5 mb-2 sm:mb-3">
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-green-100 text-green-700">
                <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>
                до {s.greenDays}дн
              </span>
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-yellow-100 text-yellow-700">
                <span className="w-2 h-2 rounded-full bg-yellow-400 inline-block"></span>
                до {s.yellowDays}дн
              </span>
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-red-100 text-red-700">
                <span className="w-2 h-2 rounded-full bg-red-500 inline-block"></span>
                от {s.yellowDays}дн
              </span>
            </div>
          )}
          {stage.error && (
            <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-700 text-xs">Ошибка загрузки: {stage.error}</p>
            </div>
          )}
          {ordersWithColors.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {ordersWithColors.map(order => {
                const formattedNum = formatOrderNumber(order.orderNumber || order.id, order.title, stage.name);
                const isRework = order.title && (
                  order.title.toLowerCase().includes('переделка') ||
                  order.title.toLowerCase().includes('бой')
                );
                return (
                  <span
                    key={order.id}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white cursor-pointer hover:opacity-80 transition-opacity"
                    style={{ backgroundColor: order.color, border: isRework ? '2px solid #8B5CF6' : 'none' }}
                    title={`${order.title || ''}${isRework ? ' (ПЕРЕДЕЛКА)' : ''}`}
                    onClick={() => loadOrderDrawings(order)}
                  >
                    {formattedNum} ({order.workingDays}д)
                  </span>
                );
              })}
            </div>
          ) : (
            <div className="text-gray-400 text-xs italic">
              {searchTerm ? 'Нет совпадений' : 'Нет заказов'}
            </div>
          )}
        </div>
      </div>
    );
  };

  // --- Индивидуальная вкладка: рендер стадии ---
  const renderIndividualStage = (stage) => {
    let ordersToShow = stage.orders || [];
    if (isFranchise && userPrefix) {
      ordersToShow = ordersToShow.filter(o => (o.orderNumber || o.id || '').toString().toLowerCase().startsWith(userPrefix));
    }
    ordersToShow = filterOrders(ordersToShow);

    const ordersWithTime = ordersToShow.map(order => {
      const movedAt = order.movedAt || order.createdAt;
      const hours = getHoursOnStage(movedAt);
      return { ...order, hoursOnStage: hours, stageColor: getIndividualColor(hours, stage.name) };
    });
    const s = settings.individual[stage.name] || { greenHours: 3, yellowHours: 6 };

    return (
      <div key={stage.stage} className="bg-white rounded-lg shadow-md border-l-4 hover:shadow-lg transition-shadow duration-200"
           style={{ borderLeftColor: stage.color }}>
        <div className="p-3 sm:p-6">
          <div className="flex items-center justify-between mb-2 sm:mb-3">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full flex-shrink-0" style={{ backgroundColor: stage.color }}></div>
              <h3 className="text-sm sm:text-lg font-semibold text-gray-800">{stage.name}</h3>
            </div>
            <div className="flex items-center space-x-1">
              <span className="text-lg sm:text-2xl font-bold" style={{ color: stage.color }}>
                {isFranchise ? ordersWithTime.length : stage.count}
              </span>
              <span className="text-xs sm:text-sm text-gray-500">зак.</span>
            </div>
          </div>
          {/* Пороги */}
          <div className="flex gap-1.5 mb-2 sm:mb-4">
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-green-100 text-green-700">
              <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>
              до {s.greenHours}ч
            </span>
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-yellow-100 text-yellow-700">
              <span className="w-2 h-2 rounded-full bg-yellow-400 inline-block"></span>
              до {s.yellowHours}ч
            </span>
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-red-100 text-red-700">
              <span className="w-2 h-2 rounded-full bg-red-500 inline-block"></span>
              от {s.yellowHours}ч
            </span>
          </div>
          {stage.error && (
            <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-700 text-xs">Ошибка загрузки: {stage.error}</p>
            </div>
          )}
          {ordersWithTime.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {ordersWithTime.map(order => {
                const formattedNum = formatOrderNumber(order.orderNumber || order.id, order.title, stage.name);
                const isRework = order.title && (
                  order.title.toLowerCase().includes('переделка') ||
                  order.title.toLowerCase().includes('бой')
                );
                return (
                  <span
                    key={order.id}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white cursor-pointer hover:opacity-80 transition-opacity"
                    style={{ backgroundColor: order.stageColor, border: isRework ? '2px solid #8B5CF6' : 'none' }}
                    title={`${order.title || ''}${isRework ? ' (ПЕРЕДЕЛКА)' : ''} — на стадии ${formatDuration(order.hoursOnStage)}`}
                    onClick={() => loadOrderDrawings(order)}
                  >
                    {formattedNum}
                    <span className="ml-1 opacity-90">{formatDuration(order.hoursOnStage)}</span>
                  </span>
                );
              })}
            </div>
          ) : (
            <div className="text-gray-400 text-xs italic">
              {searchTerm ? 'Нет совпадений' : 'Нет заказов'}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Загрузка панели заказов...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-red-800 mb-2">Ошибка загрузки панели</h3>
          <p className="text-red-700 mb-4">{error}</p>
          <button onClick={fetchDashboardData} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md transition-colors">
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return <div className="container mx-auto p-6 text-center text-gray-500">Нет данных для отображения</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {drawingModal.order && (
        <OrderDrawingsModal
          order={drawingModal.order}
          data={drawingModal.data}
          loading={drawingModal.loading}
          error={drawingModal.error}
          onClose={closeDrawingModal}
          onRetry={() => loadOrderDrawings(drawingModal.order, true)}
        />
      )}

      {showSettingsModal && (
        <SettingsModal
          settings={settings}
          onSave={handleSaveSettings}
          onClose={() => setShowSettingsModal(false)}
          saving={settingsSaving}
        />
      )}

      <div className="container mx-auto px-3 py-3 sm:px-6 sm:py-6">
        {/* Вкладки + кнопка настройки */}
        <div className="flex items-center justify-between mb-3 sm:mb-6">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('general')}
              className={`px-4 sm:px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'general'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Общее
            </button>
            {!isFranchise && (
              <button
                onClick={() => setActiveTab('individual')}
                className={`px-4 sm:px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'individual'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Индивидуально
              </button>
            )}
          </div>

          {isAdmin && (
            <button
              onClick={() => setShowSettingsModal(true)}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 shadow-sm transition-colors"
            >
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="hidden sm:inline">Настройки</span>
            </button>
          )}
        </div>

        {/* Счётчики (только Общее, не-франчайзи) */}
        {activeTab === 'general' && !isFranchise && dashboardData?.summary && (
          <div className="flex gap-2 mb-3 sm:mb-6">
            <div className="flex items-center gap-2 bg-white rounded-lg shadow-sm px-3 py-2 border border-gray-100">
              <span className="text-xs text-gray-500 whitespace-nowrap">В производстве:</span>
              <span className="text-base font-bold text-green-600">{dashboardData.summary.productionOrders || 0}</span>
            </div>
            <div className="flex items-center gap-2 bg-white rounded-lg shadow-sm px-3 py-2 border border-gray-100">
              <span className="text-xs text-gray-500 whitespace-nowrap">На складе:</span>
              <span className="text-base font-bold text-yellow-600">
                {dashboardData.warehouse
                  ? dashboardData.warehouse.filter(s => !s.name.toLowerCase().includes('готово')).reduce((t, s) => t + s.count, 0)
                  : 0}
              </span>
            </div>
            {searchTerm && (
              <div className="flex items-center gap-2 bg-white rounded-lg shadow-sm px-3 py-2 border border-blue-200">
                <span className="text-xs text-blue-600 whitespace-nowrap">Поиск: «{searchTerm}»</span>
                <button onClick={() => setSearchTerm('')} className="text-blue-400 hover:text-blue-600 text-xs">✕</button>
              </div>
            )}
          </div>
        )}

        {/* Стадии производства */}
        <div className="mb-4 sm:mb-8">
          <h2 className="text-base sm:text-xl font-semibold text-gray-800 mb-2 sm:mb-4">Стадии производства</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-6">
            {dashboardData?.stages?.length > 0 ? (
              dashboardData.stages.map(stage =>
                activeTab === 'general' ? renderGeneralStage(stage) : renderIndividualStage(stage)
              )
            ) : (
              <div className="text-gray-500">Нет данных по стадиям производства</div>
            )}
          </div>
        </div>

        {/* Складские стадии */}
        {dashboardData?.warehouse?.length > 0 && (
          <div className="mb-4 sm:mb-8">
            <h2 className="text-base sm:text-xl font-semibold text-gray-800 mb-2 sm:mb-4 flex items-center">
              <span className="mr-2">&#128230;</span>
              Складские стадии
              <span className="ml-2 px-2 py-0.5 text-xs bg-orange-100 text-orange-800 rounded-full">
                {dashboardData.warehouse.filter(s => !s.name.toLowerCase().includes('готово')).reduce((t, s) => t + s.count, 0)} заказов
              </span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-6">
              {dashboardData.warehouse.map(stage =>
                activeTab === 'general' ? renderGeneralStage(stage) : renderIndividualStage(stage)
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersDashboardPage;
